import { NgModule } from '@angular/core'
import { canActivate } from '@angular/fire/auth-guard'
import { RouterModule, Routes } from '@angular/router'
import { loggedInOnlyGuard } from '@engineering11/auth-web'
import { ConfigModule } from '@engineering11/config-web'
import { CustomerClientDataNotFoundComponent } from '@engineering11/customer-web'
import { ActAsComponent } from './components/act-as/act-as.component'
import { AccountDeactivationComponent } from './components/deactivation/deactivation.component'
import { MainLayoutComponent } from './components/layout/main-layout.component'
import { CnectFeature } from 'shared-lib'
import { StubPageComponent } from './components/stub-page/stub-page.component'
import { featureOnCanMatch } from './services/feature-flag.guard'
import { EngageSettingsComponent } from './modules/engagement/engage-settings.component'
import { Permission } from '@employer/app/config/permission.config'
import { PermissionsGuard } from '@engineering11/access-web'

/**
 * Stub-page routes (Story 3.4) — now flag-gated per Story 2.2a.
 *
 * Each new-IA leaf without a real cnect-web implementation gets TWO routes at
 * the same path:
 *   1. A `canMatch`-gated route that resolves when the flag is ON. Today this
 *      points at `StubPageComponent`; when a real component ships it replaces
 *      the stub here and the row is removed from `docs/unimplemented-pages.md`.
 *   2. A fallback route (no `canMatch`) that catches the navigation when the
 *      flag is OFF and redirects to the 404 page. A disabled feature is fully
 *      invisible — no nav item (hidden via `NavigationService.gated`) and no
 *      stub on a deep-link — matching the flag's kill-switch semantics.
 *
 * Special cases (real component / module already shipped):
 *   - `company/marketing-channels` → real lazy `MarketingChannelsModule`,
 *     gated by `employerMarketingChannels`. OFF → 404 redirect at same path.
 *   - `customer/job-application-settings` → real `JobApplicationSettingsComponent`
 *     inside `CustomerModule`, gated by `employerJobApplicationSettings` in
 *     `modules/customer/customer.routing.ts`.
 *
 * Defaults: every flag below ships OFF in the `defaultFeatures` object in
 * `projects/shared-lib/src/lib/config.ts` (`ICnectFeatures`). The remote
 * tenant-bootstrap config flips individual flags ON per environment as
 * surfaces become real — see `docs/unimplemented-pages.md` for the source.
 */
const stubGuard = canActivate(loggedInOnlyGuard('/auth/login'))
const stubRoute = (path: string, data: Record<string, unknown>) => ({
  path,
  component: StubPageComponent,
  canActivate: stubGuard.canActivate,
  data: { ...stubGuard.data, ...data },
})

/** Where a flag-OFF deep-link to a gated feature lands — the canonical 404. */
const FEATURE_OFF_REDIRECT = '/jobs/404'

/**
 * Emits a [gated, fallback] pair of routes at the same path. `gated` matches
 * when `flag` is ON (today it renders `StubPageComponent`; swap the `component`
 * for the real one when it lands). `fallback` matches when the flag is OFF and
 * redirects to the 404 page, so a disabled feature shows no stub on a deep-link
 * (its nav item is already hidden via `NavigationService.gated`).
 */
const flagGatedStubRoute = (path: string, flag: CnectFeature, data: Record<string, unknown>): Routes => [
  { ...stubRoute(path, { ...data, flag }), canMatch: [featureOnCanMatch(flag)] },
  // `pathMatch: 'full'` so this only catches the exact leaf (mirroring the
  // component route above) and never prefix-shadows a deeper gated route.
  { path, redirectTo: FEATURE_OFF_REDIRECT, pathMatch: 'full' },
]

const STUB_ROUTES: Routes = [
  ...flagGatedStubRoute('dashboard', 'employerDashboard', {
    title: 'Dashboard',
    icon: 'dashboard',
    description: 'Overview of hiring activity, pipeline metrics, and recent updates.',
    screenshotPath: 'assets/stubs/dashboard.png',
    implemented: false,
    tourId: 'dashboard.overview',
  }),
  ...flagGatedStubRoute('settings/company-profile', 'employerCompanyProfile', {
    title: 'Company Profile',
    icon: 'business',
    description: 'Manage your company identity and public-facing profile.',
    screenshotPath: 'assets/stubs/company-profile.png',
    implemented: false,
  }),
  // settings/marketing-channels is a real lazy-loaded module (MC v1.0, Story MC-13);
  // gated separately below with a stub fallback.
  ...flagGatedStubRoute('settings/personnel/access', 'employerOrgAccess', {
    title: 'Organization Access',
    icon: 'manage_accounts',
    description: 'Org Admin matrix view — users x companies x roles.',
    screenshotPath: 'assets/stubs/organization-access.png',
    implemented: false,
  }),
  ...flagGatedStubRoute('settings/personnel/team', 'employerPersonnelTeam', {
    title: 'Team',
    icon: 'groups',
    description: 'Workforce roster scoped to the active company.',
    screenshotPath: 'assets/stubs/team.png',
    implemented: false,
  }),
  ...flagGatedStubRoute('settings/personnel/org-charts', 'employerOrgCharts', {
    title: 'Org Charts',
    icon: 'account_tree',
    description: 'Visualize reporting structure.',
    screenshotPath: 'assets/stubs/org-charts.png',
    implemented: false,
  }),
  ...flagGatedStubRoute('settings/messaging-channels', 'employerMessagingChannels', {
    title: 'Messaging Channels',
    icon: 'chat',
    description: 'Configure Email / SMS / Push channels for candidate messaging.',
    screenshotPath: 'assets/stubs/messaging-channels.png',
    implemented: false,
  }),
  ...flagGatedStubRoute('settings/ai-chat', 'employerAiChatSupport', {
    title: 'AI Chat Support',
    icon: 'smart_toy',
    description: 'Configure AI chat assistance behaviors.',
    screenshotPath: 'assets/stubs/ai-chat.png',
    implemented: false,
  }),
  // Openings > Manage is a real route at /jobs/manage (inside JobsModule, Story OM-3/OM-4).
  // campaigns is a real lazy-loaded module gated separately above (next to marketing-channels).
  // Its former Dashboard/Internal stub leaves (and their flags) were removed with the nav entries.
  // Engage > Pipeline points at /jobs/list (cnect-web's existing Jobs list IS the pipeline view).
  ...flagGatedStubRoute('engagement/prospects', 'employerEngageProspects', {
    title: 'Prospects',
    icon: 'person_search',
    description: 'Pre-submission funnel — people who started applications.',
    screenshotPath: 'assets/stubs/engage-prospects.png',
    implemented: false,
  }),
  // Engage > Settings tab. Declared before the pipeline stub so the more specific
  // child path still wins; this one is an exact match on /engagement/settings.
  {
    path: 'engagement/settings',
    pathMatch: 'full',
    component: EngageSettingsComponent,
    canActivate: [PermissionsGuard],
    data: {
      permission: Permission.ManageCompanySettings,
      title: 'Pipelines Settings',
      pageTitle: { icon: 'settings', title: 'Pipelines settings' },
    },
  },
  ...flagGatedStubRoute('engagement/settings/pipeline', 'employerPipelineConfig', {
    title: 'Pipeline Configuration',
    icon: 'tune',
    description: 'Customize swim lanes and pipeline behavior per opening.',
    screenshotPath: 'assets/stubs/pipeline-config.png',
    implemented: false,
  }),
]

const routes: Routes = [
  {
    path: 'auth',
    loadChildren: () => import('./modules/authentication/authentication.module').then(m => m.AuthenticationModule),
  },
  {
    path: 'registration',
    loadChildren: () => import('./modules/registration/registration.module').then(m => m.RegistrationModule),
  },
  {
    path: 'actions',
    loadChildren: () => import('./modules/email-actions/email-actions.module').then(m => m.EmailActionsModule),
  },
  {
    path: 'public',
    loadChildren: () => import('./modules/public/public.module').then(m => m.PublicModule),
  },
  {
    path: 'about',
    loadChildren: () => import('./modules/home/home.module').then(m => m.HomeModule),
    ...canActivate(loggedInOnlyGuard('/auth/login')),
  },
  {
    path: 'account-deactivated',
    component: AccountDeactivationComponent,
  },
  {
    path: 'privacy',
    redirectTo: 'public/privacy',
  },
  {
    path: 'terms',
    data: { title: 'Terms of Use' },
    redirectTo: 'public/terms',
  },
  {
    path: '',
    component: MainLayoutComponent,
    children: [
      {
        path: 'home/404',
        pathMatch: 'full',
        redirectTo: '/jobs/404',
      },
      {
        path: 'home',
        redirectTo: 'jobs/list',
        pathMatch: 'full',
      },
      {
        path: 'jobs',
        loadChildren: () => import('./modules/jobs/jobs.module').then(m => m.JobsModule),
        ...canActivate(loggedInOnlyGuard('/auth/login')),
      },
      {
        path: 'candidate-pool',
        loadChildren: () => import('./modules/candidate-pool/candidate-pool.module').then(m => m.CandidatePoolModule),
        ...canActivate(loggedInOnlyGuard('/auth/login')),
      },
      /*
       * CNCT-6779 — every company settings screen now lives under /settings. The
       * CustomerModule is mounted as a child of that path (see settings.routing.ts)
       * rather than on one of its own, and these entries keep the old URLs working:
       * bookmarks, links already emailed out, and anything saved in a browser.
       *
       * The two renamed screens need a full match each; everything else maps 1:1, which
       * a PREFIX redirect does in one line — /customer/departments/detail lands on
       * /settings/departments/detail without enumerating sub-paths. Order matters: the
       * full matches must precede the prefix, or the prefix swallows them.
       */
      { path: 'customer/account', redirectTo: 'settings/company-account', pathMatch: 'full' },
      { path: 'customer/home', redirectTo: 'settings/company-account', pathMatch: 'full' },
      { path: 'customer', redirectTo: 'settings' },
      { path: 'company/profile', redirectTo: 'settings/company-profile', pathMatch: 'full' },
      { path: 'company/settings/messaging-channels', redirectTo: 'settings/messaging-channels', pathMatch: 'full' },
      { path: 'company/settings/ai-chat', redirectTo: 'settings/ai-chat', pathMatch: 'full' },
      { path: 'company', redirectTo: 'settings' },
      {
        path: 'customer-clients',
        loadChildren: () => import('./modules/customer-clients/customer-clients.module').then(m => m.CustomerClientModule),
        ...canActivate(loggedInOnlyGuard('/auth/login')),
      },
      {
        path: 'account',
        loadChildren: () => import('./modules/account/account.module').then(m => m.AccountModule),
        ...canActivate(loggedInOnlyGuard('/auth/login')),
      },
      {
        path: 'users',
        loadChildren: () => import('./modules/users/users.module').then(m => m.UsersModule),
        ...canActivate(loggedInOnlyGuard('/auth/login')),
      },
      {
        path: 'notification',
        loadChildren: () => import('./modules/notification/notification.module').then(m => m.NotificationModule),
        ...canActivate(loggedInOnlyGuard('/auth/login')),
      },
      {
        path: 'c',
        loadChildren: () => import('./modules/layout-container/layout-container.module').then(m => m.LayoutContainerModule),
        ...canActivate(loggedInOnlyGuard('/auth/login')),
      },
      {
        path: 'reports',
        loadChildren: () => import('./modules/reports/reports.module').then(m => m.ReportsModule),
        ...canActivate(loggedInOnlyGuard('/auth/login')),
      },
      // Marketing Channels v1.0 — lazy-loaded module (Story MC-13).
      // Gated by `employerMarketingChannels` (Story 2.2a). Flag OFF →
      // canMatch fails, falls through to the 404 redirect declared just below.
      // Defaults OFF until backend OAuth round-trip is verified end-to-end.
      {
        path: 'settings/marketing-channels',
        canMatch: [featureOnCanMatch('employerMarketingChannels')],
        loadChildren: () => import('./modules/marketing-channels/marketing-channels.module').then(m => m.MarketingChannelsModule),
        ...canActivate(loggedInOnlyGuard('/auth/login')),
      },
      { path: 'settings/marketing-channels', redirectTo: FEATURE_OFF_REDIRECT, pathMatch: 'full' },
      // Campaigns — lazy-loaded module. Gated by its own `employerCampaigns` flag
      // (decoupled from `employerJobPromotions`, which now only gates the legacy
      // /jobs/promotions surface); flag OFF → canMatch fails, falls through to the
      // 404 redirect below.
      {
        path: 'campaigns',
        canMatch: [featureOnCanMatch('employerCampaigns')],
        loadChildren: () => import('./modules/campaigns/campaigns.module').then(m => m.CampaignsModule),
        ...canActivate(loggedInOnlyGuard('/auth/login')),
      },
      { path: 'campaigns', redirectTo: FEATURE_OFF_REDIRECT, pathMatch: 'full' },
      // Email groups (CNCT-6818) — lazy-loaded module. Gated by `employerEmailGroups`;
      // flag OFF → canMatch fails and the fallback just below redirects the deep link to
      // the canonical 404. Declared before the `settings` prefix route for the same reason
      // STUB_ROUTES are: that entry is a prefix match and would otherwise swallow this path.
      {
        path: 'settings/email-groups',
        canMatch: [featureOnCanMatch('employerEmailGroups')],
        loadChildren: () => import('./modules/email-groups/email-groups.module').then(m => m.EmailGroupsModule),
        ...canActivate(loggedInOnlyGuard('/auth/login')),
      },
      { path: 'settings/email-groups', redirectTo: FEATURE_OFF_REDIRECT, pathMatch: 'full' },
      // ----- Story 3.4: stub routes for unimplemented new-IA leaves -----
      // These sit at /settings/* now (CNCT-6779), so they MUST be declared before the
      // settings module below: that entry is a prefix match and would otherwise consume
      // them, handing every stub path to the module's own 404.
      ...STUB_ROUTES,
      // All Company Settings — the hub landing plus, as children, every company settings
      // screen (CNCT-6779). Always available to logged-in users; the screens keep their
      // own guards inside CustomerModule.
      {
        path: 'settings',
        loadChildren: () => import('./modules/settings/settings.module').then(m => m.SettingsModule),
        ...canActivate(loggedInOnlyGuard('/auth/login')),
      },
      // ----- Story 4.3: Messages top-level route promotion. -----
      // Re-routes /messages to the existing candidate-pool messages surface as a
      // pragmatic interim destination until a dedicated messages route module ships.
      // Tracked in docs/nav-inventory.md → Messages section.
      {
        path: 'messages',
        redirectTo: 'candidate-pool/candidates',
        pathMatch: 'full',
      },

      {
        path: '',
        redirectTo: 'jobs/list',
        pathMatch: 'full',
      },
    ],
  },
  {
    path: 'tenant-not-found',
    component: CustomerClientDataNotFoundComponent,
  },
  { path: 'config', loadChildren: () => ConfigModule },
  {
    path: 'act-as',
    component: ActAsComponent,
  },
  {
    path: '',
    redirectTo: 'home',
    pathMatch: 'full',
  },
  { path: '404', pathMatch: 'full', redirectTo: '/jobs/404' },
]

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      useHash: true,
      scrollPositionRestoration: 'enabled',
      scrollOffset: [0, 0],
      anchorScrolling: 'enabled',
      onSameUrlNavigation: 'reload',
      // enableTracing: true, // Comment in for nice router debugging
    }),
  ],
  // providers: [{ provide: RouteReuseStrategy, useClass: E11RouteReuseStrategy }],
  exports: [RouterModule],
})
export class AppRoutingModule {}
